<template>
  <card-canvas :hide-back-button="projectCreated">
    <div class="project-new">
      <div class="project-new__title">
        <h4 v-if="!projectCreated">Create a new project</h4>
        <h4 v-else class="scripty">Invite user to this project&hellip;</h4>
        <avatar-gallery 
          v-if="projectCreated"
          :people="assignedPeople" 
          :max-num-avatars="6" 
          :size="28" 
          stack-tightly
        />
      </div>
      <loading-placeholder v-if="isLoading" :lines="10" class="loading" />
      <template v-else>
        <template v-if="!projectCreated">
          <div class="step">
            <h4>1. Name it</h4>
            <el-input ref="nameRef" v-model="name" :is-focused="focusName" maxlength="100" placeholder="e.g. Marketing Initiatives, Bug Fixes, HR Tasks" />
          </div>
          <div class="step">
            <h4>2. Select a workflow or <a href="" class="just-a-link" @click.prevent="showCreateWorkflowDrawer=true">make a new one&hellip;</a></h4>
            <entity-workflow-selector
              v-model="selectedWorkflowID" 
              :workflows="workflows" 
              :entity-type="EEntities.PROJECTS"
              :default-value="defaultSelectedWorkflowID"
            />
          </div>
          <div v-if="$permissions.jobsFeatureEnabled && selectedWorkflowHasJobs" class="step">
            <h4>3. Workflow Jobs</h4>
            <job-toggler
              v-model:jobs-enabled="enableJobs"
              :status-job-mappings="selectedWorkflow.listStatusJobMappings"
              :statuses="selectedWorkflow.listStatuses"
              :jobs="selectedWorkflow.listJobs"
              :entity-type="EEntities.PROJECTS"
            />
          </div>
          <dd-button 
            :is-loading="isSubmitting"
            :color="ColorAssistant.ProjectColor"
            size="large"
            new-icon
            @click="create"
          >
            Create Project
          </dd-button>
        </template>
        <template v-else>
          <div class="step">
            <entity-membership-manager
              :entity-type="EEntities.PROJECTS"
              :entity-id="newProjectID" 
              hide-success-messages
              @memberships-updated="(val) => assignedPeople = val"
            />
          </div>
          <dd-button 
            :color="ColorAssistant.ProjectColor"
            size="large"
            @click="goToProjectLanding()"
          >
            Go to my project!
          </dd-button>
        </template>
      </template>
    </div>
    <create-workflow-drawer 
      v-model:show-drawer="showCreateWorkflowDrawer"
      :entity-type="EEntities.PROJECTS"
      @workflow-created="(id) => handleWorkflowCreation(id)"
    />
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import CardCanvas from '@/layouts/card-canvas.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import {
  IItemForSelection,
  IPersonBase,
  IWorkflowForSelection,
} from '@/api/types/response.types'
import { IInternalProjectsInputCreate } from '@/api/types/request.types'
import { EEntities } from '@/types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import Avatar from '@/components/global/avatar.vue'

export default defineComponent({

  components: {
    CardCanvas,
    Avatar,
  },

  data() {
    return {
      EEntities,
      RouteAssistant,
      ColorAssistant,
      focusName: true,
      isLoading: true,
      isSubmitting: false,
      workflows: [] as IWorkflowForSelection[],
      showCreateWorkflowDrawer: false,
      newProjectID: 0,
      assignedPeople: [] as IPersonBase[],
      defaultSelectedWorkflowID: null as any,

      // Input values
      name: '',
      selectedWorkflowID: null as any,
      enableJobs: this.$permissions.jobsFeatureEnabled,
    }
  },
  head: {
    title: 'New Project',
  },

  computed: {
    projectCreated(): boolean {
      return this.newProjectID !== 0
    },
    selectedWorkflow(): IWorkflowForSelection {
      if (!this.selectedWorkflowID) {
        return null
      }
      return this.workflows.find(w => w.id === this.selectedWorkflowID)
    },
    selectedWorkflowHasJobs(): boolean {
      if (!this.selectedWorkflow) {
        return false
      }
      return this.selectedWorkflow.listJobs.length > 0
    },
  },

  async created() {
    this.isLoading = true
    await this.getWorkflows()
    this.isLoading = false
    await this.$nextTick()
    this.selectedWorkflowID = this.workflows[0].id

    if (
      this.$route.query.workflow_id &&
      this.workflows.some(
        w => w.id.toString() === this.$route.query.workflow_id.toString(),
      )
    ) {
      // Query values are strings; workflow IDs are numbers
      this.defaultSelectedWorkflowID = Number(this.$route.query.workflow_id)
    }

    this.$nextTick(() => {
      // @ts-ignore
      this.$refs.nameRef.focus()
    })
  },

  methods: {
    goToProjectLanding() {
      LocalStore.setShowInfoOnEntityLanding(this.newProjectID)
      RouteAssistant.goToProjectLanding(
        this.$router,
        this.$route.params.accountId,
        this.newProjectID,
        this.$permissions,
      )
    },
    async handleWorkflowCreation(id: number) {
      await this.getWorkflows()

      const selectedWorkflow = this.workflows.find(w => w.id === id)
      const selectedIndex = this.workflows.findIndex(w => w.id === id)

      this.workflows.splice(selectedIndex, 1)
      this.workflows.unshift(selectedWorkflow)

      this.selectedWorkflowID = id
    },
    async getWorkflows() {
      this.workflows = await InternalProjectsAPI.get_Workflows()
    },
    async create() {
      if (this.name.trim().length === 0) {
        this.focusName = false
        await this.$nextTick()
        this.focusName = true
        this.$messaging.showError('Please enter a name.')
        return
      }
      this.isSubmitting = true
      try {
        const data = {
          name: this.name,
          workflowID: this.selectedWorkflowID,
          enableJobs: this.enableJobs,
        } as IInternalProjectsInputCreate
        const response = await InternalProjectsAPI.post_Create(data)
        this.newProjectID = response.id
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.project-new {
  .scripty {
    font-size: 26px !important;
  }

  &__title {
    background-color: var(--color-background-header);
    top: $navbar-desktop-height;
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(8px);
    margin-bottom: 20px;
    border-bottom: $border--dotted-medium;

    .avatar-gallery {
      margin-left: auto;
    }

    @include media-breakpoint-down(sm) {
      top: $navbar-mobile-height;
    }

    > .ddi {
      color: var(--color-text-third);
      top: -4px;
    }
  }

  .step {
    margin-bottom: 40px;
  }

  .dd-button {
    margin-left: auto;
  }
}
</style>
